import { useEffect, useRef, useState } from "react";
import useSWRMutation from "swr/mutation";
import { ImageIcon, PlusIcon } from "@radix-ui/react-icons";
import { selectFileReader } from "commons";
import { formDataFetcher } from "api/swr";
import { spineStaticThumbUrl } from "commons/cdn";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Skeleton from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { useEditorStore } from "../../../../../store/editorStore";
import { SkinThumbnail } from "../../../../../components/SkinThumbnail";

type CoverPreview = {
  original: string;
  w: number;
  h: number;
};

/** 仅影响详情预览，不写回数据 */
type PreviewSource = "custom" | "original";

interface UpdateSkuThumbProps {
  skuId: string;
  skinName: string;
  thumbAssetId?: string;
}

/**
 * 动态 SKU 自定义封面：强制 512×512，上传到 spine-static-skus/t/，写入 thumbAssetId
 */
export function UpdateSkuThumb({
  skuId,
  skinName,
  thumbAssetId,
}: UpdateSkuThumbProps) {
  const updateSkuField = useEditorStore((s) => s.updateSkuField);

  const [open, setOpen] = useState(false);
  const [cover, setCover] = useState<CoverPreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewSource, setPreviewSource] = useState<PreviewSource>("custom");
  const coverRef = useRef<HTMLInputElement>(null);

  const { trigger, isMutating } = useSWRMutation(
    "/spine-templates/upload-sku-thumb",
    formDataFetcher,
  );

  const coverError = cover && (cover.w !== 512 || cover.h !== 512);
  const currThumbUrl = thumbAssetId
    ? spineStaticThumbUrl(thumbAssetId)
    : "";
  const showCustom = !!currThumbUrl && previewSource === "custom";

  // 切换 SKU / 封面变更时，默认回到自定义封面预览
  useEffect(() => {
    setPreviewSource("custom");
  }, [skuId, thumbAssetId]);

  const reset = () => {
    setCover(null);
    setFile(null);
    if (coverRef.current) coverRef.current.value = "";
  };

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) reset();
  };

  const handleSelectCover = () => {
    if (coverRef.current) {
      coverRef.current.value = "";
      coverRef.current.click();
    }
  };

  const handleCoverFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const next = event.target.files?.[0];
    if (!next) return;
    const data = (await selectFileReader(next)) as CoverPreview;
    setCover(data);
    setFile(next);
  };

  const handleUpdate = async () => {
    if (!file || !cover || coverError || isMutating) return;
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = (await trigger(formData)) as { thumbAssetId: string };
      updateSkuField(skuId, { thumbAssetId: res.thumbAssetId });
      toast({ title: "封面已更新", description: "请记得保存模板" });
      onOpenChange(false);
    } catch (e: unknown) {
      toast({
        variant: "destructive",
        title: "上传失败",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleRemove = () => {
    if (!thumbAssetId || isMutating) return;
    updateSkuField(skuId, { thumbAssetId: null });
    toast({ title: "已移除自定义封面", description: "请记得保存模板" });
    onOpenChange(false);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        {showCustom ? (
          <img
            src={currThumbUrl}
            alt="sku thumb"
            className="w-[200px] h-[200px] object-contain border rounded-lg bg-muted/30"
          />
        ) : (
          <SkinThumbnail
            skinName={skinName}
            size={200}
            className="border rounded-lg"
          />
        )}
        {currThumbUrl && (
          <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-background/80 border">
            {showCustom ? "自定义封面" : "ZIP 原图"}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {currThumbUrl && (
          <div className="inline-flex rounded-md border p-0.5 bg-muted/40">
            <Button
              type="button"
              variant={previewSource === "custom" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setPreviewSource("custom")}
            >
              自定义封面
            </Button>
            <Button
              type="button"
              variant={previewSource === "original" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setPreviewSource("original")}
            >
              ZIP 原图
            </Button>
          </div>
        )}

        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1"
              disabled={!skuId}
            >
              <ImageIcon className="h-3.5 w-3.5" />
              {currThumbUrl ? "更换封面" : "上传封面"}
            </Button>
          </DialogTrigger>
        <DialogContent className="max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Update cover</DialogTitle>
            <DialogDescription>
              须正好 512×512。上传后写入 SKU 的 thumbAssetId，保存模板后生效。
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col">
            <div className="flex gap-4">
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  From ZIP:
                </div>
                <SkinThumbnail
                  skinName={skinName}
                  size={96}
                  className="border rounded"
                />
              </div>
              {currThumbUrl && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">
                    Curr cover:
                  </div>
                  <img
                    src={currThumbUrl}
                    alt="curr cover"
                    className="w-24 h-24 object-contain border rounded cursor-pointer"
                    onClick={handleSelectCover}
                  />
                </div>
              )}
            </div>

            <div className="-mx-1 my-1 mt-3 h-px bg-muted" />

            <div className="grid gap-4 py-4">
              {cover ? (
                <img
                  alt="new cover"
                  src={cover.original}
                  onClick={handleSelectCover}
                  className={cn(
                    "w-32 h-32 object-contain border rounded cursor-pointer",
                    coverError && "border-destructive",
                  )}
                />
              ) : (
                <Skeleton
                  className={cn(
                    "w-32 h-32 flex flex-col items-center justify-center cursor-pointer",
                  )}
                  onClick={handleSelectCover}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span className="text-center text-xs">Select cover</span>
                </Skeleton>
              )}
            </div>

            <input
              type="file"
              ref={coverRef}
              className="hidden"
              accept="image/*"
              onChange={handleCoverFileChange}
            />

            <DialogFooter>
              <div className="flex gap-2 w-full flex-wrap">
                <Button variant="outline" onClick={reset} disabled={isMutating}>
                  Reset
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRemove}
                  disabled={!thumbAssetId || isMutating}
                >
                  Remove
                </Button>
                <Button
                  className="flex-grow"
                  onClick={() => void handleUpdate()}
                  disabled={!cover || !!coverError || isMutating || !file}
                >
                  {isMutating ? "Uploading..." : "Update"}
                </Button>
              </div>
              {coverError && (
                <div className="text-destructive text-sm w-full mt-2">
                  size require: 512x512
                </div>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      </div>

      {thumbAssetId && (
        <p className="text-[10px] font-mono text-muted-foreground break-all text-center">
          t:{thumbAssetId}
        </p>
      )}
    </div>
  );
}
